import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { useAppAuthed } from "../../lib/gate";
import { LockedBlur } from "../LockedBlur";
import { StepStart } from "./StepStart";
import { StepProduct } from "./StepProduct";
import { StepIul } from "./StepIul";
import { StepFia } from "./StepFia";
import { StepResults } from "./StepResults";
import { StepCompare } from "./StepCompare";
import { AssumptionsDrawer } from "./AssumptionsDrawer";
import { ExportBar } from "./ExportBar";
import { Warnings, Disclosures } from "./YearTable";

const STEPS = ["Start", "Product", "Configure", "Results", "Compare"];
const STORE = "ex_rollover_state";
const SID = "ex_rollover_sid";
const clone = (o) => JSON.parse(JSON.stringify(o));

const initialInputs = (s) => ({
  product: "iul", scenario: "baseline",
  client: { source: "401k", amount: s.client.amount, age: s.client.age, state: "GA", federal_rate: s.tax.federal_rate, state_rate: s.tax.state_rates.GA,
    direct_rollover: true, roth_5yr: true, roth_basis_pct: s.tax.roth_basis_pct, goal: "tax_free_income", income_start_age: s.client.retire_age,
    plan_to_age: s.client.plan_to_age, illustrate_to_age: s.client.illustrate_to_age, income_mode: "maximize", income_amount: 40000 },
  qualified: { annual_return: s.qualified.returns.baseline, annual_fee: s.qualified.annual_fee },
  iul: clone(s.iul_presets.baseline), fia: clone(s.fia_presets.baseline),
});

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]) return `Check "${d[0].loc?.slice(-1)[0]}": ${d[0].msg}`;
  return "Couldn't calculate. Check your inputs.";
};

const Progress = ({ step, setStep }) => (
  <ol data-testid="ro-progress" className="grid grid-cols-5 gap-px bg-exodus-border border border-exodus-border mb-10">
    {STEPS.map((s, i) => (
      <li key={s}>
        <button type="button" data-testid={`ro-step-${i}`} onClick={() => setStep(i)}
          className={`w-full px-2 py-3 text-left transition-colors ${i === step ? "bg-exodus-gold text-exodus-bg" : i < step ? "bg-exodus-elevated" : "bg-exodus-surface text-exodus-muted"}`}>
          <span className="block text-[10px] font-mono">0{i + 1}</span>
          <span className="block text-xs sm:text-sm truncate">{s}</span>
        </button>
      </li>
    ))}
  </ol>
);

export default function RolloverWizard() {
  const authed = useAppAuthed();
  const [settings, setSettings] = useState(null);
  const [inputs, setInputs] = useState(null);
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const sidRef = useRef(sessionStorage.getItem(SID));
  const inputsRef = useRef(null);
  inputsRef.current = inputs;

  useEffect(() => {
    api.get("/rollover/settings").then((r) => {
      setSettings(r.data);
      const saved = JSON.parse(sessionStorage.getItem(STORE) || "null");
      setInputs(saved?.inputs || initialInputs(r.data));
      setStep(saved?.step || 0);
    }).catch(() => setError("Couldn't load the calculator. Please refresh."));
  }, []);

  useEffect(() => { if (inputs) sessionStorage.setItem(STORE, JSON.stringify({ inputs, step })); }, [inputs, step]);

  useEffect(() => {
    if (!inputs) return undefined;
    const t = setTimeout(() => {
      api.post("/rollover/calculate", inputs).then((r) => { setResult(r.data); setError(""); }).catch((e) => setError(errText(e)));
    }, 600);
    return () => clearTimeout(t);
  }, [inputs]);

  const saveNow = useCallback(async () => {
    const r = await api.post("/rollover/scenarios", { inputs: inputsRef.current, scenario_id: sidRef.current });
    sidRef.current = r.data.id;
    sessionStorage.setItem(SID, r.data.id);
    return r.data.id;
  }, []);

  // Signed-in users' results are stored automatically so advisors see them in the portal.
  useEffect(() => {
    if (!authed || step < 3 || !result || error) return undefined;
    const t = setTimeout(() => { saveNow().catch(() => {}); }, 1500);
    return () => clearTimeout(t);
  }, [authed, step >= 3, result, error, saveNow]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!inputs || !settings) return <p data-testid="ro-loading" className="text-sm text-exodus-muted py-20 text-center">{error || "Loading the rollover calculator…"}</p>;

  const patch = (key) => (p) => setInputs((s) => ({ ...s, [key]: { ...s[key], ...p } }));
  const setTop = (p) => setInputs((s) => ({ ...s, ...p }));
  const onScenario = (sc) => setInputs((s) => ({ ...s, scenario: sc, iul: { ...clone(settings.iul_presets[sc]), mec_acknowledged: false },
    fia: clone(settings.fia_presets[sc]), qualified: { ...s.qualified, annual_return: settings.qualified.returns[sc] } }));
  const go = (i) => { setStep(Math.max(0, Math.min(STEPS.length - 1, i))); window.scrollTo({ top: document.getElementById("rollover-wizard")?.offsetTop - 90, behavior: "smooth" }); };

  return (
    <section id="rollover-wizard" data-testid="rollover-wizard" className="max-w-5xl mx-auto px-6 lg:px-12 pb-32">
      <Progress step={step} setStep={go} />
      <AssumptionsDrawer inputs={inputs} result={result} />
      {error && <p data-testid="ro-error" className="mb-6 border border-red-500/50 bg-red-500/10 p-3 text-sm">{error}</p>}

      <div className="bg-exodus-surface/40 border border-exodus-border p-6 sm:p-10">
        {step === 0 && <StepStart c={inputs.client} set={patch("client")} settings={settings} />}
        {step === 1 && <StepProduct inputs={inputs} setTop={setTop} onScenario={onScenario} />}
        {step === 2 && (inputs.product === "iul"
          ? <StepIul p={inputs.iul} set={patch("iul")} result={result} />
          : <StepFia f={inputs.fia} set={patch("fia")} result={result} />)}
        {step === 2 && result && <div className="mt-8"><Warnings items={result.warnings} only={inputs.product === "iul" ? ["mec", "iul_taxable", "indirect", "roth_nonqualified"] : ["indirect"]} /></div>}
        {step >= 3 && result && (
          <LockedBlur testid="ro-results-locked" label="Your rollover results are ready">
            {step === 3 ? <StepResults inputs={inputs} result={result} onAckMec={(v) => patch("iul")({ mec_acknowledged: v })} />
              : <StepCompare result={result} product={inputs.product} />}
          </LockedBlur>
        )}
        {step >= 3 && <div className="mt-10"><ExportBar ensureSaved={saveNow} bookingUrl={settings.booking_url} /></div>}

        <div className="flex justify-between gap-4 mt-10">
          <button type="button" data-testid="ro-back" onClick={() => go(step - 1)} disabled={step === 0}
            className="inline-flex items-center gap-2 text-sm text-exodus-muted hover:text-exodus-gold disabled:opacity-30"><ArrowLeft size={14} />Back</button>
          {step < STEPS.length - 1 && (
            <button type="button" data-testid="ro-next" onClick={() => go(step + 1)} className="btn-gold inline-flex items-center gap-2">
              {step === 2 ? "See my results" : step === 3 ? "Compare all three" : "Continue"} <ArrowRight size={14} />
            </button>
          )}
        </div>
        <Disclosures />
      </div>
    </section>
  );
}
